import { useRef, useState } from "react";

/**
 * Owns the "what happens to existing cardholders?" decision that a changed
 * expiration rule forces.
 *
 * Lifted out of EditPass deliberately. This control flow has been got wrong
 * twice — once by saving first and offering afterwards, once by letting a
 * second save walk past the offer — and buried in a thousand-line component it
 * could not be tested directly. Here each rule is one function.
 *
 * The rules:
 *
 *   - Nothing is written until the question is answered. `hold` returns true
 *     when it has parked a save and opened the question; the caller must not
 *     save when it does.
 *   - While the question is open, no save may start, however many times the
 *     button is pressed.
 *   - The question only appears when the SERVER says the rule actually changed.
 *     A recoloured card never sees it.
 *   - A failed preview costs nobody their card edit: no question, ordinary save.
 *   - Cancel writes nothing and keeps the editor as it was.
 */
export function useExpiryDecision({ previewExpiration, savePass, applyExpiration }) {
  const [decision, setDecision] = useState(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const pendingRef = useRef(null);
  // A ref, not the state above: `isCommitting` is a render-time value, so two
  // clicks dispatched before React re-renders both read false and both start a
  // save — and a second bulk apply would open a second run whose snapshot no
  // longer describes what the first one overwrote. A ref updates synchronously
  // and closes that window. (Login.jsx guards double submits the same way.)
  const committingRef = useRef(false);

  const isHolding = decision !== null;

  /**
   * Call before saving. Returns true when the save has been held and the
   * question opened — in which case the caller must not write anything.
   */
  const hold = async (payload) => {
    if (isHolding || committingRef.current) return true;

    let preview = null;
    try {
      preview = await previewExpiration(payload);
    } catch {
      // No question rather than no save: a preview that fails must not cost
      // the merchant their card edit.
      return false;
    }

    if (!preview?.changed || !preview.total) return false;

    pendingRef.current = payload;
    setDecision(preview);
    return true;
  };

  /**
   * Answer it. The card's new rule is saved here and only here; the bulk apply
   * runs after that save, because the server applies the rule it has on file.
   */
  const commit = async (applyToExisting) => {
    const payload = pendingRef.current;
    if (!payload || committingRef.current) return null;

    committingRef.current = true;
    setIsCommitting(true);
    try {
      await savePass(payload);

      if (applyToExisting) {
        await applyExpiration(payload);
      }

      const answered = decision;
      pendingRef.current = null;
      setDecision(null);
      return answered;
    } finally {
      committingRef.current = false;
      setIsCommitting(false);
    }
  };

  const cancel = () => {
    if (committingRef.current) return;

    pendingRef.current = null;
    setDecision(null);
  };

  return { decision, isHolding, isCommitting, hold, commit, cancel };
}
